import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import React from 'react';
import { pathManager } from 'routes/consts';

import { Description } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { makeStyles } from '@mui/styles';

import {
  GetDashboardV2VInfoQuery,
  JobMatchStageEnum,
  useGetTalentsReportLazyQuery,
} from '@libs/graphql-types';
import { RouterButton } from '@libs/ui/components/button';
import { RouterLink } from '@libs/ui/components/typography';

import { ReportPreparingModal, useOpenReportPreparingModal } from './index';

// Define a helper type for a single job, handling potential null/undefined
// import { NonNullable } from 'types'; // Remove incorrect import
type DashboardJobType = NonNullable<
  GetDashboardV2VInfoQuery['jobs']
>['data'][number];

interface OpenJobsProps {
  jobs: DashboardJobType[];
  isLoading?: boolean;
}

const useStyles = makeStyles((theme) => ({
  card: {
    width: '100%',
  },
  button: {
    height: '30px',
    padding: '0 8px',
    marginLeft: 'auto',
  },
}));

const OpenJobs: React.FC<OpenJobsProps> = ({
  jobs = [], // Default to empty array if jobs prop is not provided
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const openReportModal = useOpenReportPreparingModal();
  const [getJobsReport, { loading: isReportLoading }] =
    useGetTalentsReportLazyQuery({
      fetchPolicy: 'network-only',
      onCompleted: () => {
        enqueueSnackbar('Report was sent to your email', {
          variant: 'success',
        });
      },
    });

  const handleReportClick = async () => {
    await getJobsReport();
    openReportModal();
  };

  // Helper function to format date or return '-'
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {
      return '-';
    }
    try {
      return format(new Date(dateString), 'MM-dd-yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-'; // Return '-' in case of formatting error
    }
  };

  return (
    <Card elevation={0} className={classes.card}>
      <CardHeader
        title={<Typography variant="h6">Open Jobs</Typography>}
        action={
          <Button
            variant="outlined"
            color="info"
            disabled={isReportLoading}
            endIcon={<Description />}
            className={classes.button}
            onClick={handleReportClick}
          >
            Report
          </Button>
        }
      />
      <CardContent>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Role Title</TableCell>
                <TableCell>Applications</TableCell>
                <TableCell>Placements</TableCell>
                <TableCell>Offers Pending</TableCell>
                <TableCell>Interviews Pending</TableCell>
                <TableCell>Onboards</TableCell>
                <TableCell>Created date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => {
                // Reduce matches to counts by stage
                const matchCounts = (job.matches || []).reduce<
                  Record<string, number>
                >((acc, match) => {
                  const stage = match?.stage;
                  if (stage) {
                    acc[stage] = (acc[stage] || 0) + 1;
                  }
                  return acc;
                }, {});
                return (
                  <TableRow key={job.id}>
                    <TableCell>
                      <RouterLink
                        to={pathManager.company.job.generatePath({
                          id: job.id,
                        })}
                        color="info.main"
                        variant="body2"
                      >
                        {job.name}
                      </RouterLink>
                    </TableCell>
                    <TableCell>
                      {matchCounts[JobMatchStageEnum.Application] || 0}
                    </TableCell>
                    <TableCell>
                      {matchCounts[JobMatchStageEnum.Placement] || 0}
                    </TableCell>
                    <TableCell>
                      {matchCounts[JobMatchStageEnum.Offer] || 0}
                    </TableCell>
                    <TableCell>
                      {(matchCounts[JobMatchStageEnum.InternalInterview] || 0) +
                        (matchCounts[JobMatchStageEnum.ClientInterview] || 0)}
                    </TableCell>
                    <TableCell>
                      {matchCounts[JobMatchStageEnum.Onboarding] || 0}
                    </TableCell>
                    <TableCell>{formatDate(job.posted_at)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="center" mt={4}>
          <RouterButton
            to={pathManager.company.jobBoard.getRoute()}
            variant="outlined"
            color="info"
          >
            SHOW ALL JOBS
          </RouterButton>
        </Box>
      </CardContent>
      <ReportPreparingModal />
    </Card>
  );
};

export default OpenJobs;
